/* eslint-disable max-len */
import { Button, IconButton, Popover, Typography } from "@mui/material";
import { lighten, styled } from "@mui/material/styles";

export const BookContainer = styled("div")(({ theme }) => ({
  textAlign: "left",
  userSelect: "none",
  fontSize: "0.9em",
  padding: "16px 24px",
  lineHeight: "1.5",

  ["span:not(.hljs *)"]: {
    color: theme.palette.text.book,
  },

  img: {
    display: "inline-block",
    maxWidth: "100%",
    height: "auto",
  },
}));

export const HeaderContainer = styled("div")<{ isModal: boolean }>(({ isModal, theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isModal ? "flex-start" : "space-between",
  marginBottom: theme.spacing(2),
  gap: theme.spacing(3),
}));

export const HeaderLeftContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

export const HeaderRightContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
});

export const StyledButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  height: "100%",

  color: theme.custom.palette.semantic.buttonIconColor,
  ["&:hover"]: {
    color: theme.custom.palette.semantic.buttonIconHoverColor,
  },
}));

export const StyledPlayButton = styled(StyledButton)(({ theme }) => ({
  color: theme.custom.palette.semantic.buttonIconColor,
  ["&:hover"]: {
    color: theme.custom.palette.semantic.buttonIconHoverColor,
  },
}));

export const StyledTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== "fontSize",
})<{ component: React.ReactNode; fontSize?: number }>(({ theme, fontSize }) => ({
  color: theme.custom.palette.semantic.blue,
  fontWeight: 600,
  fontSize: fontSize !== undefined ? `${fontSize}px` : "16px",
}));

export const StyledBodyTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== "fontSize",
})<{ component: React.ReactNode; fontSize?: number }>(({ theme, fontSize }) => ({
  color: theme.palette.text.book,
  fontWeight: 400,
  fontSize: fontSize !== undefined ? `${fontSize}px` : "14px",
  margin: "revert",

  ["a"]: {
    overflowWrap: "break-word",
    textDecoration: "none",
    color: theme.custom.palette.semantic.anchorColor,

    "&:hover, a:focus": {
      color: lighten(theme.custom.palette.semantic.anchorColor, 0.3),
    },
  },

  'a[id^="gfm-footnotes-ref"]': {
    paddingLeft: "5px",
  },

  ["p, span"]: {
    overflowWrap: "break-word",
  },

  ["ul li:has(ul), ul li:has(ol)"]: {
    listStyleType: "none",
  },

  li: {
    marginBottom: theme.spacing(1),
  },

  "li > ul": {
    marginTop: theme.spacing(1),
  },

  ["ol, ul"]: {
    color: theme.palette.text.book,
  },

  ".tableWrapper": {
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.grey["700"],
    },

    "::-webkit-scrollbar": {
      height: "5px",
    },
  },

  'p[style*="background"]': {
    background: "none !important",
  },

  pre: {
    borderColor: theme.custom.palette.semantic.codeBorder,
    borderStyle: "dashed",
    borderWidth: "1px",
    borderRadius: "6px",
    maxWidth: "100%",
    whiteSpace: "pre",
    lineHeight: 0,
    overflowX: "auto",
    overflowY: "hidden",
  },

  // Our old codeBlock pre elements that had no code elements inside
  "pre:not(:has(code))": {
    textAlign: "left",
    backgroundColor: theme.custom.palette.semantic.codeBg,
    borderColor: theme.custom.palette.semantic.codeBorder,
    borderStyle: "dashed",
    borderWidth: "1px",
    borderRadius: "6px",
    color: theme.custom.palette.semantic.codeColor,
    boxSizing: "border-box",
    display: "block",
    fontFamily: "source-code-pro, Menlo, Monaco, 'Courier New', Consolas, monospace",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    lineHeight: 1.5,
  },

  code: {
    textAlign: "left",
    backgroundColor: theme.custom.palette.semantic.codeBg,
    borderColor: theme.custom.palette.semantic.codeBorder,
    borderStyle: "dashed",
    borderWidth: "1px",
    borderRadius: "6px",
    color: theme.custom.palette.semantic.codeColor,
    boxSizing: "border-box",
    display: "inline-block",
    fontFamily: "source-code-pro, Menlo, Monaco, 'Courier New', Consolas, monospace",
    // Padding below is for inline code elements
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    paddingRight: theme.spacing(0.75),
    paddingLeft: theme.spacing(0.75),
    lineHeight: 1.5,
  },

  "pre code": {
    whiteSpace: "pre-wrap",
    borderColor: "currentColor",
    borderStyle: "none",
    borderWidth: 0,
    borderRadius: 0,
    display: "block",
    width: "100%",
    minWidth: "max-content",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },

  ".gfm-alerts": {
    borderLeft: "5px solid #ccc",
    paddingLeft: "15px",
    paddingRight: "10px",
    backgroundColor: theme.custom.palette.semantic.gfmAlertBg,
  },

  ".gfm-alerts-note": {
    borderLeftColor: "#4493f8",
  },

  ".gfm-alerts-note svg path": {
    fill: "#4493f8",
  },

  ".gfm-alerts-tip": {
    borderLeftColor: "#238636",
  },

  ".gfm-alerts-tip svg path": {
    fill: "#238636",
  },

  ".gfm-alerts-important": {
    borderLeftColor: "#ab7df8",
  },

  ".gfm-alerts-important svg path": {
    fill: "#ab7df8",
  },

  ".gfm-alerts-warning": {
    borderLeftColor: "#d29922",
  },

  ".gfm-alerts-warning svg path": {
    fill: "#d29922",
  },

  ".gfm-alerts-caution": {
    borderLeftColor: "#f85149",
  },

  ".gfm-alerts-caution svg path": {
    fill: "#f85149",
  },

  ".gfm-alerts-title": {
    position: "relative",
    top: "5px",
  },

  ".gfm-alerts-title svg": {
    display: "inline-block",
    verticalAlign: "middle",
    marginRight: "5px",
  },

  ".gfm-alerts p:last-child": {
    position: "relative",
    top: "-5px",
  },

  ".katex": {
    display: "block",

    "math + math": {
      paddingTop: `${theme.spacing(2)} !important`,
    },
  },

  ".katex + .katex": {
    paddingTop: `${theme.spacing(2)} !important`,
  },

  "p .katex, span .katex, li .katex, a .katex, em .katex, strong .katex, i .katex, b .katex, small .katex, sub .katex, sup .katex":
    {
      display: "inline",
    },

  ".indent-1": {
    marginLeft: `${theme.spacing(1)} !important`,
  },

  ".indent-2": {
    marginLeft: `${theme.spacing(2)} !important`,
  },

  ".indent-3": {
    marginLeft: `${theme.spacing(3)} !important`,
  },

  ".indent-4": {
    marginLeft: `${theme.spacing(4)} !important`,
  },

  ".indent-5": {
    marginLeft: `${theme.spacing(5)} !important`,
  },

  ".indent-6": {
    marginLeft: `${theme.spacing(6)} !important`,
  },

  ".indent-7": {
    marginLeft: `${theme.spacing(7)} !important`,
  },

  ".indent-8": {
    marginLeft: `${theme.spacing(8)} !important`,
  },

  "ol.arabic-period-bullets": {
    listStyleType: "decimal",
  },

  "ol.arabic-parenr-bullets": {
    listStyleType: "none",
    counterReset: "list-counter",

    "& > li": {
      counterIncrement: "list-counter",
    },
    "& > li::marker": {
      content: 'counter(list-counter, decimal) ") "',
      letterSpacing: "1px",
    },
  },

  "ol.roman-uc-period-bullets": {
    listStyleType: "upper-roman",
  },

  "ol.alpha-uc-period-bullets": {
    listStyleType: "upper-alpha",
  },

  "ol.alpha-lc-parenr-bullets": {
    listStyleType: "none",
    counterReset: "list-counter",
    "& > li": {
      counterIncrement: "list-counter",
    },
    "& > li::marker": {
      content: 'counter(list-counter, lower-alpha) ") "',
      letterSpacing: "1px",
    },
  },

  "ol.alpha-lc-period-bullets": {
    listStyleType: "lower-alpha",
  },

  "ol.roman-lc-period-bullets": {
    listStyleType: "lower-roman",
  },

  "ul.filled-round-bullets, ul.hollow-round-bullets, ul.filled-square-bullets, ul.hollow-square-bullets, ul.star-bullets, ul.arrow-bullets, ul.tick-bullets, ul.line-bullets":
    {
      listStyleType: "none",
    },

  "ul.filled-round-bullets > li": {
    listStyleType: "disc",
  },
  "ul.hollow-round-bullets > li": {
    listStyleType: "circle",
  },
  "ul.filled-square-bullets > li": {
    listStyleType: "square",
  },

  "ul.hollow-square-bullets > li, ul.star-bullets > li, ul.arrow-bullets > li, ul.tick-bullets > li, ul.line-bullets > li":
    {
      listStyleType: "none",
      position: "relative",
    },

  "ul.hollow-square-bullets > li::before, ul.star-bullets > li::before, ul.arrow-bullets > li::before, ul.tick-bullets > li::before, ul.line-bullets > li::before":
    {
      width: "1rem",
      height: "1rem",
      LineHeight: "1rem",
      verticalAlign: "baseline",
      fontSize: "0.8rem",
      position: "absolute",
      textAlign: "left",
      marginLeft: "-0.9rem",
    },

  "ul.hollow-square-bullets > li::before": {
    content: '"\\25A1"',
  },
  "ul.star-bullets > li::before": {
    content: '"\\2724"',
    top: "1px",
  },
  "ul.arrow-bullets > li::before": {
    content: '"\\23F5"',
    top: "1px",
    marginLeft: "-0.8rem",
  },
  "ul.tick-bullets > li::before": {
    content: '"\\2713"',
    top: "1px",
  },
  "ul.line-bullets > li::before": {
    content: '"\\2013"',
    top: "1px",
  },
}));

export const StyledPopover = styled(Popover)(({ theme }) => ({
  marginTop: theme.spacing(1),
  [".MuiPaper-root"]: {
    overflow: "visible",
    boxShadow: theme.custom.shadows[5],
    ["&:before"]: {
      content: '""',
      display: "block",
      position: "absolute",
      top: 0,
      left: 14,
      width: 10,
      height: 10,
      background: theme.custom.palette.semantic.modalBg,
      transform: "translateY(-50%) rotate(45deg)",
      zIndex: 0,
    },
  },
}));

export const ExpandButton = styled(Button)(({ theme }) => ({
  color: theme.custom.palette.semantic.buttonOutlinedColor,
  borderColor: theme.custom.palette.semantic.buttonOutlinedBorder,

  ["svg"]: {
    color: theme.custom.palette.semantic.buttonOutlinedColor,
  },

  "&:hover": {
    color: theme.custom.palette.semantic.buttonOutlinedHoverColor,
    borderColor: theme.custom.palette.semantic.buttonOutlinedHoverBorder,
    ["svg"]: {
      color: theme.custom.palette.semantic.buttonOutlinedHoverColor,
    },
  },

  "&:focus": {
    color: theme.custom.palette.semantic.buttonOutlinedFocusColor,
    borderColor: theme.custom.palette.semantic.buttonOutlinedFocusBorder,
    ["svg"]: {
      color: theme.custom.palette.semantic.buttonOutlinedFocusColor,
    },
  },
}));
